import { useSelector, useDispatch } from "react-redux";
import {
  idSelector,
  permissionSelector,
} from "../../../features/cartransferSlice";
import { Form, Input, InputNumber, Button, Row, Col } from "antd";
import { setEntity } from "../../../features/cartransferSlice";
import {
  parseFieldsToState,
  parseStateToFields,
} from "../../../utils/parseFormfields";
import UploadFile from "../../../utils/uploadFile";
import {
  FileTextOutlined,
  DollarOutlined,
  SearchOutlined,
  PictureOutlined,
  FilePdfOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";

// ── Shared design tokens ─────────────────────────────────────────────
const ACCENT = "#7c3aed";
const LIGHT  = "#faf5ff";
const BORDER = "#ddd6fe";

const PanelHeader = ({ icon, title, hasDoc }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "10px 16px",
      background: LIGHT,
      border: `1px solid ${BORDER}`,
      borderRadius: 10,
      borderLeft: `4px solid ${ACCENT}`,
      marginBottom: 0,
    }}
  >
    <span style={{ color: ACCENT, fontSize: 16 }}>{icon}</span>
    <span style={{ fontWeight: 700, fontSize: 14, color: ACCENT, flex: 1 }}>
      {title}
    </span>
    {hasDoc && (
      <CheckCircleOutlined style={{ color: "#22c55e", fontSize: 15 }} />
    )}
  </div>
);

const FieldSection = ({ icon, label }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 6,
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: 1.2,
      textTransform: "uppercase",
      color: "#b0b0b0",
      marginBottom: 10,
      marginTop: 4,
      paddingBottom: 6,
      borderBottom: "1px solid #f0f0f0",
    }}
  >
    {icon && <span style={{ fontSize: 11 }}>{icon}</span>}
    {label}
  </div>
);

const fi = { marginBottom: 10 };

const PermissionStep = ({ label }) => {
  const dispatch = useDispatch();
  const entity = "permission";
  const permission = useSelector(permissionSelector);
  const contract = useSelector((state) => state.cartransfer.contract);
  const cartransferId = useSelector(idSelector);

  const hasDoc = !!(permission?.docUrl || permission?.appraisal || permission?.commune);

  const getFolio = () =>
    alert("Pendiente, ir a www.puertomonttchile.cl a extraer datos");

  return (
    <div>
      {/* Panel header */}
      <PanelHeader
        icon={<FileTextOutlined />}
        title={label || "Permiso de circulación"}
        hasDoc={hasDoc}
      />

      {/* Form body */}
      <div
        style={{
          border: `1px solid ${ACCENT}30`,
          borderTop: "none",
          borderRadius: "0 0 10px 10px",
          padding: "16px 16px 10px",
          background: "#fff",
        }}
      >
        <Form
          name={entity}
          onFieldsChange={(_, allFields) => {
            const state = parseFieldsToState(allFields);
            dispatch(setEntity({ entity, data: state }));

            const permissionForContract = {
              ...(state?.commune ? { commune: state.commune } : {}),
              ...(state?.year ? { year: state.year } : {}),
              ...(state?.siicode ? { siicode: state.siicode } : {}),
              ...(state?.appraisal ? { appraisal: state.appraisal } : {}),
            };
            const contractUpdates = {};
            if (Object.keys(permissionForContract).length > 0) {
              contractUpdates.permission = {
                ...(contract?.permission || {}),
                ...permissionForContract,
              };
            }
            if (state?.appraisal) {
              const appraisalNum = parseInt(state.appraisal);
              const newPrice = !isNaN(appraisalNum) && appraisalNum > 0 ? appraisalNum : 1;
              contractUpdates.price = newPrice;
              contractUpdates.tax = Math.round(newPrice * 0.015);
            }
            if (Object.keys(contractUpdates).length > 0) {
              dispatch(setEntity({ entity: "contract", data: contractUpdates }));
            }
          }}
          fields={parseStateToFields({
            ...permission,
            ...(contract?.permission || {}),
          })}
          layout="vertical"
        >
          {/* ── SUBIR ARCHIVOS ────────────────────────────── */}
          <FieldSection icon={<FilePdfOutlined />} label="Subir documentos" />
          <Row gutter={12} style={{ marginBottom: 10 }}>
            <Col span={12}>
              <div
                style={{
                  border: `1.5px dashed ${permission?.photoUrl ? "#22c55e" : "#d1d5db"}`,
                  borderRadius: 8,
                  padding: "10px 12px",
                  background: permission?.photoUrl ? "#f0fdf4" : "#fafafa",
                  transition: "all 0.2s",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    marginBottom: 6,
                    fontSize: 11,
                    color: "#888",
                    fontWeight: 600,
                  }}
                >
                  <PictureOutlined />
                  Foto respaldo
                </div>
                <Form.Item style={{ marginBottom: 0 }}>
                  <UploadFile
                    type=".jpeg, .jpg, .png"
                    entity={entity}
                    action="/cartransfers/folder"
                    label="Subir foto"
                    cartransferId={cartransferId}
                  />
                </Form.Item>
              </div>
            </Col>
            <Col span={12}>
              <div
                style={{
                  border: `1.5px dashed ${permission?.docUrl ? "#22c55e" : "#d1d5db"}`,
                  borderRadius: 8,
                  padding: "10px 12px",
                  background: permission?.docUrl ? "#f0fdf4" : "#fafafa",
                  transition: "all 0.2s",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    marginBottom: 6,
                    fontSize: 11,
                    color: "#888",
                    fontWeight: 600,
                  }}
                >
                  <FilePdfOutlined />
                  PDF permiso
                </div>
                <Form.Item style={{ marginBottom: 0 }}>
                  <UploadFile
                    type="application/pdf"
                    entity={entity}
                    action="/parse-docs/fetch-permission"
                    label="Subir PDF"
                    cartransferId={cartransferId}
                  />
                </Form.Item>
              </div>
            </Col>
          </Row>
          {(permission?.docUrl || permission?.photoUrl) && (
            <div style={{ marginBottom: 10 }}>
              {permission?.docUrl && (
                <a href={permission.docUrl} target="_blank" rel="noreferrer"
                  style={{ fontSize: 12, color: ACCENT, marginRight: 12 }}>
                  Ver PDF
                </a>
              )}
            </div>
          )}

          {/* ── EXTRACCIÓN ────────────────────────────────── */}
          <FieldSection icon={<SearchOutlined />} label="Extracción automática" />
          <Row gutter={12} align="bottom">
            <Col span={16}>
              <Form.Item name="verification" label="Código de verificación" style={fi}>
                <Input size="middle" placeholder="Código del permiso" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label=" " style={fi}>
                <Button
                  type="primary"
                  block
                  onClick={getFolio}
                  style={{ background: ACCENT, borderColor: ACCENT, height: 32 }}
                >
                  Extraer
                </Button>
              </Form.Item>
            </Col>
          </Row>

          {/* ── DATOS FISCALES ────────────────────────────── */}
          <FieldSection icon={<DollarOutlined />} label="Datos fiscales" />
          <Row gutter={12}>
            <Col span={14}>
              <Form.Item name="commune" label="Comuna" style={fi}>
                <Input size="middle" placeholder="Puerto Montt" />
              </Form.Item>
            </Col>
            <Col span={10}>
              <Form.Item name="year" label="Año permiso" style={fi}>
                <Input size="middle" placeholder="2024" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="siicode" label="Código SII" style={fi}>
                <Input size="middle" placeholder="Código" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="appraisal" label="Tasación ($)" style={fi}>
                <InputNumber
                  min={0}
                  style={{ width: "100%" }}
                  size="middle"
                  formatter={(v) => v ? `$ ${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ".") : ""}
                  parser={(v) => v.replace(/\$\s?|(\.*)/g, "")}
                  placeholder="0"
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </div>
    </div>
  );
};

export default PermissionStep;
